import type { CmmdForgeCloudProjectRemovalRow } from "../environments/primary/cloudProjectCatalog";
import { buildCmmdRuntimeEnvironmentId } from "../environments/cmmdRuntimeEnvironmentId";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../hooks/useLocalStorage";
import * as Schema from "effect/Schema";

import { readCloudProjectDesiredState } from "./cmmdCloudProjectRowMapping";
import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "./cmmdForgeRuntimeListTypes";
import {
  mapCmmdStatusToRuntimeStatus,
  UNPROVISIONED_CLOUD_PROJECT_STATUS,
  type ForgeRuntimeStatus,
} from "./forgeRuntimeStatus";

// v5 drops the legacy Git-staleness decision field. Older cache keys
// are ignored so a partial snapshot cannot invent an owner Environment.
const CACHE_KEY = "forge:cmmd-cloud-project-catalog:v5";

const PersistedRowSchema = Schema.Struct({
  runtimeId: Schema.NullOr(Schema.String),
  cmmdOrganizationId: Schema.Number,
  cmmdProjectId: Schema.Number,
  cmmdEnvironmentId: Schema.NullOr(Schema.String),
  registeredAt: Schema.optional(Schema.NullOr(Schema.String)),
  projectName: Schema.String,
  githubRepoFullName: Schema.NullOr(Schema.String),
  workspaceRoot: Schema.NullOr(Schema.String),
  iconUrl: Schema.optional(Schema.NullOr(Schema.String)),
  icon: Schema.optional(Schema.NullOr(Schema.String)),
  status: Schema.String,
  desiredState: Schema.optional(Schema.NullOr(Schema.String)),
  environmentId: Schema.NullOr(Schema.String),
  removal: Schema.optional(
    Schema.NullOr(
      Schema.Struct({
        operationId: Schema.String,
        deletePlanningProject: Schema.Boolean,
        status: Schema.Literals(["pending", "running", "retryable"]),
        phase: Schema.Literals([
          "destroy_runtime",
          "purge_execution_state",
          "delete_planning_project",
          "unregister",
          "complete",
        ]),
        lastErrorCode: Schema.NullOr(Schema.String),
        lastErrorMessage: Schema.NullOr(Schema.String),
        updatedAt: Schema.String,
      }),
    ),
  ),
});

const PersistedCacheSchema = Schema.Struct({
  rows: Schema.Array(PersistedRowSchema),
  environments: Schema.Array(
    Schema.Struct({
      runtimeId: Schema.String,
      cmmdOrganizationId: Schema.Number,
      cmmdUserId: Schema.Number,
      // `optional` so a cache written before Environments carried names still
      // parses. Dropping it instead would throw away every existing cache and
      // cost every returning user a cold first paint.
      displayName: Schema.optional(Schema.NullOr(Schema.String)),
      userFirstName: Schema.optional(Schema.NullOr(Schema.String)),
      environmentId: Schema.String,
      cmmdEnvironmentId: Schema.NullOr(Schema.String),
      status: Schema.String,
      desiredState: Schema.NullOr(Schema.String),
    }),
  ),
  fetchedAt: Schema.String,
});

type PersistedCache = typeof PersistedCacheSchema.Type;

/**
 * Drops the persisted catalog snapshot outright, rather than persisting an
 * empty one -- an empty *persisted* snapshot still reads back as "loaded,
 * zero rows" (`stale: true`) on the next read, exactly what a workspace
 * switch must not show while the new workspace's catalog is still in flight.
 */
export function clearCachedRows(): void {
  removeLocalStorageItem(CACHE_KEY);
}

export function loadCachedRows(): {
  rows: CloudProjectCatalogRow[];
  environments: CloudEnvironmentCatalogRow[];
  fetchedAt: string;
} | null {
  try {
    const cached = getLocalStorageItem(CACHE_KEY, PersistedCacheSchema);
    if (!cached) return null;
    const rows: CloudProjectCatalogRow[] = cached.rows.map((r) => ({
      runtimeId: r.runtimeId,
      cmmdOrganizationId: r.cmmdOrganizationId,
      cmmdProjectId: r.cmmdProjectId,
      registeredAt: r.registeredAt ?? null,
      projectName: r.projectName,
      githubRepoFullName: r.githubRepoFullName,
      workspaceRoot: r.workspaceRoot,
      iconUrl: r.iconUrl ?? null,
      icon: r.icon ?? null,
      status:
        r.runtimeId === null
          ? UNPROVISIONED_CLOUD_PROJECT_STATUS
          : (mapCmmdStatusToRuntimeStatus(r.status) as ForgeRuntimeStatus),
      desiredState: readCloudProjectDesiredState(r.desiredState),
      environmentId: r.runtimeId === null ? null : buildCmmdRuntimeEnvironmentId(r.runtimeId),
      cmmdEnvironmentId: r.cmmdEnvironmentId,
      resolvedRef: null,
      removal: (r.removal as CmmdForgeCloudProjectRemovalRow | null | undefined) ?? null,
    }));
    const environments = cached.environments.map((environment) => ({
      runtimeId: environment.runtimeId,
      cmmdOrganizationId: environment.cmmdOrganizationId,
      cmmdUserId: environment.cmmdUserId,
      displayName: environment.displayName ?? null,
      userFirstName: environment.userFirstName ?? null,
      cmmdEnvironmentId: environment.cmmdEnvironmentId,
      status: mapCmmdStatusToRuntimeStatus(environment.status) as ForgeRuntimeStatus,
      desiredState: readCloudProjectDesiredState(environment.desiredState),
      environmentId: buildCmmdRuntimeEnvironmentId(environment.runtimeId),
    }));
    return { rows, environments, fetchedAt: cached.fetchedAt };
  } catch {
    return null;
  }
}

export function persistRows(
  rows: readonly CloudProjectCatalogRow[],
  environments: readonly CloudEnvironmentCatalogRow[],
  fetchedAt: string,
): void {
  try {
    const doc: PersistedCache = {
      rows: rows.map((r) => ({
        runtimeId: r.runtimeId,
        cmmdOrganizationId: r.cmmdOrganizationId,
        cmmdProjectId: r.cmmdProjectId,
        cmmdEnvironmentId: r.cmmdEnvironmentId,
        registeredAt: r.registeredAt,
        projectName: r.projectName,
        githubRepoFullName: r.githubRepoFullName,
        workspaceRoot: r.workspaceRoot,
        iconUrl: r.iconUrl,
        icon: r.icon,
        status: r.status,
        environmentId: r.environmentId,
        removal: r.removal,
      })),
      environments,
      fetchedAt,
    };
    setLocalStorageItem(CACHE_KEY, doc, PersistedCacheSchema);
  } catch {
    // persistence failure is non-fatal
  }
}
